import { useAppContext } from "../../../2_ksbHub/context/appContext";
import { useBszFinderContext } from "../context/bszContext";
import bszService from "../services";

export default function useBszActions() {
  const { currentUser } = useAppContext();
  const { setSnackbar, setRooms } = useBszFinderContext();

  /**
   * =========================================================
   * ✅ SNACKBAR
   * =========================================================
   */
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  /**
   * =========================================================
   * ✅ RESERVE SLOT
   * =========================================================
   */
  const handleReserve = async (slotId, action) => {
    if (!currentUser) return;

    try {
      await bszService.reserveSlot(slotId, action);
    } catch (err) {
      console.error("❌ reserve failed:", err);
      showSnackbar("Fehler bei der Reservation", "error");
    }
  };

  /**
   * =========================================================
   * ✅ SAVE ROOM
   * =========================================================
   */
  const handleSaveRoom = async (roomData, closeDialog) => {
    try {
      if (roomData.id) {
        await bszService.updateRoom(roomData);
        showSnackbar("Raum wurde aktualisiert");
      } else {
        await bszService.createRoom(roomData);
        showSnackbar("Raum wurde erstellt");
      }

      closeDialog?.();
    } catch (err) {
      console.error("❌ save room failed:", err);
      showSnackbar("Fehler beim Speichern", "error");
    }
  };

  /**
   * =========================================================
   * ✅ DELETE ROOM (OPTIMISTIC)
   * =========================================================
   */
  const handleDeleteRoom = async (roomId) => {
    let previousRooms;

    setRooms((prev) => {
      previousRooms = prev;
      return prev.filter((r) => r.id !== roomId);
    });

    try {
      await bszService.deleteRoom(roomId);
      showSnackbar("Raum gelöscht");
    } catch (err) {
      console.error("❌ delete failed → rollback", err);

      setRooms(previousRooms);
      showSnackbar("Fehler beim Löschen", "error");
    }
  };

  /**
   * =========================================================
   * ✅ GENERATE SLOTS
   * =========================================================
   */
  const handleGenerateSlots = async (roomId, studentSlots, teacherSlots) => {
    try {
      await bszService.generateSlots({
        roomId,
        studentSlots,
        teacherSlots,
      });

      showSnackbar("Slots wurden generiert");
    } catch (err) {
      console.error("❌ generate slots failed:", err);
      showSnackbar("Fehler beim Generieren der Slots", "error");
    }
  };

  /**
   * =========================================================
   * ✅ ✅ ✅ CORE: MOVE ROOM (FINAL SYSTEM)
   * =========================================================
   */
  const moveRoom = ({ roomId, section, row, col }) => {
    let updatedRow = [];

    setRooms((prev) => {
      if (!Array.isArray(prev)) return prev;

      /**
       * ✅ get affected row
       */
      const sameRow = prev
        .filter(
          (r) =>
            r.section === section &&
            (r.layout_row ?? 0) === row &&
            r.id !== roomId,
        )
        .sort((a, b) => (a.layout_col ?? 0) - (b.layout_col ?? 0));

      const dragged = prev.find((r) => r.id === roomId);
      if (!dragged) return prev;

      /**
       * ✅ insert
       */
      const insertIndex = Math.max(0, Math.min(col, sameRow.length));
      sameRow.splice(insertIndex, 0, {
        ...dragged,
        section,
        layout_row: row,
      });

      /**
       * ✅ rebuild row
       */
      updatedRow = sameRow.map((room, index) => ({
        ...room,
        layout_row: row,
        layout_col: index,
        section,
      }));

      /**
       * ✅ merge back
       */
      return prev.map((room) => {
        const updated = updatedRow.find((r) => r.id === room.id);
        return updated ? updated : room;
      });
    });

    /**
     * ✅ 🔥 SEND FULL ROW TO BACKEND
     */
    bszService.updateRoomLayoutBulk(updatedRow).catch((err) => {
      console.error("❌ bulk update failed:", err);
    });
  };

  /**
   * =========================================================
   * ✅ EXPORT
   * =========================================================
   */
  return {
    handleReserve,
    handleSaveRoom,
    handleDeleteRoom,
    handleGenerateSlots,

    moveRoom, // ✅ ONLY layout function now

    showSnackbar,
    handleCloseSnackbar,
  };
}
